import {
  type RemovableRef,
  type Serializer,
  StorageSerializers,
  useLocalStorage,
} from '@vueuse/core';

/// Functions

/**
 * Reactive wrapper around localStorage. Reads `id` on first access, writes on every assignment;
 * default serializer is `StorageSerializers.object` (JSON). Pinia state uses this so localStorage
 * is the source of truth on reload.
 *
 * @param id - LocalStorage key.
 * @param defaultValue - Returned when the key is absent or unparseable.
 * @param serializer - Optional custom serializer; defaults to JSON-based `object`.
 * @returns A VueUse `RemovableRef<T>` reactive to the stored value.
 */
export function useLS<T>(
  id: string,
  defaultValue: T,
  serializer?: Serializer<unknown>,
): RemovableRef<T> {
  return useLocalStorage(id, defaultValue, {
    serializer: serializer || StorageSerializers.object,
  });
}

/**
 * JSON-roundtrip deep clone. Loses non-serializable values — functions drop, `undefined` entries
 * drop, Dates become strings, Maps/Sets drop, cycles throw. Adequate for OData JSON payloads;
 * unsafe for prototyped or reactive objects.
 */
export function clone(data: unknown): unknown {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Parse an OData date / date-time value into a JS Date for date-picker binding. Accepts ISO strings
 * (`2024-01-15`, `2024-01-15T00:00:00.000Z`) and existing Date instances. Empty / unparseable →
 * null.
 */
export function parseToDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a picker `Date` back to a `YYYY-MM-DD` string for the OData payload. SQL Server accepts
 * this for both `date` and `datetime` columns; using local-date components avoids the timezone
 * shift `toISOString()` introduces near midnight.
 */
export function dateToString(d: Date | null | undefined): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Saves a fetched file through the browser's download flow. The object URL is revoked on a delay
 * because some browsers start reading it only after `click()` returns.
 *
 * @param blob - File contents.
 * @param filename - Name the browser saves it under.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
